/**
 * Maintenance Interval Optimization Engine
 * Part of OptimalAvailability Studio - Auto-Generated Optimization capabilities
 *
 * Provides optimization strategies for maintenance scheduling:
 * - Age-Based Replacement (ARP)
 * - Block Replacement
 * - Condition-Based Maintenance (CBM)
 * - Group Maintenance Scheduling
 * - Delay Time Model
 * - Sensitivity Analysis
 */

class IntervalOptimization {
  /**
   * Optimize Age-Based Preventive Replacement interval using Weibull distribution
   * Minimizes cost rate: C(t) = [Cp*R(t) + Cu*F(t)] / [t*R(t) + MTBF_truncated]
   *
   * @param {Object} params
   * @param {number} params.shape - Weibull shape parameter (β)
   * @param {number} params.scale - Weibull scale parameter (η)
   * @param {number} params.plannedCost - Cost of preventive replacement (Cp)
   * @param {number} params.unplannedCost - Cost of failure replacement (Cu)
   * @param {number} params.missionTime - System mission time constraint
   * @returns {Object} { optimalInterval, costRate, availability, savings }
   */
  optimizeAgeReplacement(params) {
    const { shape, scale, plannedCost, unplannedCost, missionTime } = params;

    if (!this._validateWeibullParams(shape, scale)) {
      throw new Error('Invalid Weibull parameters');
    }
    if (plannedCost <= 0 || unplannedCost <= 0) {
      throw new Error('Costs must be positive');
    }

    // Search bounds for optimal interval
    const tMin = scale * 0.1;
    const tMax = scale * 5;

    // Cost function to minimize
    const costFunction = (t) => {
      const R_t = this.weibullReliability(t, shape, scale);
      const F_t = 1 - R_t;

      if (R_t <= 0) return Infinity;

      // Calculate expected time in system
      const mtbf_truncated = this._weibullMTBFTruncated(t, shape, scale);
      const expectedSystemTime = t * R_t + mtbf_truncated;

      if (expectedSystemTime <= 0) return Infinity;

      // Cost rate: [Cp*R(t) + Cu*F(t)] / expectedSystemTime
      const numerator = plannedCost * R_t + unplannedCost * F_t;
      return numerator / expectedSystemTime;
    };

    // Find optimal interval using golden section search
    const optimalInterval = this.goldenSectionSearch(costFunction, tMin, tMax, 1e-4);

    // Calculate metrics at optimal interval
    const R_opt = this.weibullReliability(optimalInterval, shape, scale);
    const F_opt = 1 - R_opt;
    const costRateOptimal = costFunction(optimalInterval);

    // Run-to-failure cost rate (only unplanned failures)
    const mtbf = this.weibullMTBF(shape, scale);
    const costRateRunToFailure = unplannedCost / mtbf;

    // Availability at optimal interval
    const mttr = 2; // Assume 2 hours MTTR (can be parameterized)
    const availability = mtbf / (mtbf + mttr);

    return {
      optimalInterval: parseFloat(optimalInterval.toFixed(4)),
      costRate: parseFloat(costRateOptimal.toFixed(4)),
      availability: parseFloat(availability.toFixed(4)),
      savingsVsRunToFailure: parseFloat(((costRateRunToFailure - costRateOptimal) / costRateRunToFailure * 100).toFixed(2)) + '%',
      reliabilityAtOptimal: parseFloat(R_opt.toFixed(4)),
      failureProbabilityAtOptimal: parseFloat(F_opt.toFixed(4))
    };
  }

  /**
   * Optimize Block Replacement Strategy (fixed-interval replacement)
   * Minimizes expected cost per unit time regardless of component age
   *
   * @param {Object} params
   * @param {number} params.shape - Weibull shape parameter (β)
   * @param {number} params.scale - Weibull scale parameter (η)
   * @param {number} params.plannedCost - Cost of planned replacement
   * @param {number} params.unplannedCost - Cost of failure replacement
   * @param {Object} params.blockInterval - Search range { min, max }
   * @returns {Object} { optimalBlock, costPerUnitTime, expectedReplacements }
   */
  optimizeBlockReplacement(params) {
    const { shape, scale, plannedCost, unplannedCost, blockInterval } = params;

    if (!this._validateWeibullParams(shape, scale)) {
      throw new Error('Invalid Weibull parameters');
    }
    if (!blockInterval || blockInterval.min <= 0 || blockInterval.max <= blockInterval.min) {
      throw new Error('Invalid block interval range');
    }

    const costFunction = (T) => {
      // Expected number of failures in [0, T]
      const F_T = 1 - this.weibullReliability(T, shape, scale);

      // Expected cost per cycle = Cp + Cu * F(T)
      // Cost per unit time = [Cp + Cu*F(T)] / T
      return (plannedCost + unplannedCost * F_T) / T;
    };

    const optimalBlock = this.goldenSectionSearch(
      costFunction,
      blockInterval.min,
      blockInterval.max,
      1e-4
    );

    const F_opt = 1 - this.weibullReliability(optimalBlock, shape, scale);
    const costPerUnitTime = costFunction(optimalBlock);
    const expectedReplacements = F_opt;

    return {
      optimalBlock: parseFloat(optimalBlock.toFixed(4)),
      costPerUnitTime: parseFloat(costPerUnitTime.toFixed(4)),
      expectedReplacements: parseFloat(expectedReplacements.toFixed(4)),
      failureProbabilityInBlock: parseFloat(F_opt.toFixed(4))
    };
  }

  /**
   * Optimize Condition-Based Maintenance (CBM) using P-F interval analysis
   * Net benefit = avoided failures × failure cost - inspection costs
   *
   * @param {Object} params
   * @param {number} params.pfInterval - Time from detection (P) to failure (F)
   * @param {number} params.inspectionCost - Cost per inspection
   * @param {number} params.failureCost - Cost of failure
   * @param {number} params.inspectionInterval - Current inspection interval
   * @param {number} params.detectionProbability - Probability of detecting defect [0,1]
   * @returns {Object} { optimalInspectionInterval, netBenefit, detectionRate }
   */
  optimizeCBM(params) {
    const { pfInterval, inspectionCost, failureCost, inspectionInterval, detectionProbability } = params;

    if (pfInterval <= 0 || inspectionCost <= 0 || failureCost <= 0) {
      throw new Error('P-F interval, costs must be positive');
    }
    if (detectionProbability < 0 || detectionProbability > 1) {
      throw new Error('Detection probability must be in [0, 1]');
    }

    // Maximum economical inspection interval = P-F interval / 2
    const maxOptimalInterval = pfInterval / 2;

    const costFunction = (T) => {
      if (T <= 0 || T > pfInterval) return Infinity;

      // Expected inspections per unit time = 1/T
      const inspectionsPerUnit = 1 / T;

      // Probability of detecting defect before failure
      const detectionRateActual = detectionProbability;

      // Expected failures per unit time (if inspection misses defect)
      const failureRate = (1 - detectionRateActual) / pfInterval;

      // Net cost = inspection costs + failure costs
      const netCost = inspectionsPerUnit * inspectionCost + failureRate * failureCost;

      return netCost;
    };

    const optimalInspectionInterval = this.goldenSectionSearch(
      costFunction,
      pfInterval * 0.05,
      maxOptimalInterval,
      1e-4
    );

    const netBenefit = failureCost * detectionProbability - inspectionCost / optimalInspectionInterval;
    const detectionRate = detectionProbability * (1 - Math.exp(-optimalInspectionInterval / pfInterval));

    return {
      optimalInspectionInterval: parseFloat(optimalInspectionInterval.toFixed(4)),
      netBenefit: parseFloat(netBenefit.toFixed(4)),
      detectionRate: parseFloat(detectionRate.toFixed(4)),
      inspectionsPerYear: parseFloat((365 / optimalInspectionInterval).toFixed(2)),
      recommendedInterval: optimalInspectionInterval <= pfInterval / 2 ?
        `Every ${Math.round(optimalInspectionInterval)} hours` :
        'Increase P-F interval or detection probability'
    };
  }

  /**
   * Optimize Group Maintenance interval (find common interval for multiple tasks)
   * Minimizes total cost across grouped maintenance tasks
   *
   * @param {Array} tasks - Array of { id, interval, cost, penalty, shape, scale }
   * @returns {Object} { optimalGroupInterval, totalCost, taskSchedule }
   */
  optimizeGroupInterval(tasks) {
    if (!Array.isArray(tasks) || tasks.length === 0) {
      throw new Error('Tasks array must not be empty');
    }

    // Validate tasks
    tasks.forEach((task, idx) => {
      if (!task.id || task.cost <= 0 || !this._validateWeibullParams(task.shape, task.scale)) {
        throw new Error(`Invalid task at index ${idx}`);
      }
    });

    // Get all individual optimal intervals
    const individualIntervals = tasks.map(task => task.interval || this.weibullMTBF(task.shape, task.scale));

    // Search around the mean individual interval
    const meanInterval = individualIntervals.reduce((a, b) => a + b) / individualIntervals.length;
    const searchMin = meanInterval * 0.5;
    const searchMax = meanInterval * 2;

    const costFunction = (groupInterval) => {
      let totalCost = 0;

      tasks.forEach(task => {
        // How many times task runs in group interval
        const timesInGroup = groupInterval / task.interval;

        // Cost of running task at group interval (may be more or less frequently)
        const taskCost = timesInGroup * task.cost;

        // Penalty for not running at optimal time
        const deviation = Math.abs(groupInterval - task.interval);
        const penalty = task.penalty ? (deviation / task.interval) * task.penalty : 0;

        totalCost += taskCost + penalty;
      });

      return totalCost;
    };

    const optimalGroupInterval = this.goldenSectionSearch(
      costFunction,
      searchMin,
      searchMax,
      1e-4
    );

    // Generate task schedule
    const taskSchedule = tasks.map(task => ({
      taskId: task.id,
      originalInterval: task.interval,
      groupInterval: optimalGroupInterval,
      frequency: optimalGroupInterval / task.interval,
      costPerGroup: (optimalGroupInterval / task.interval) * task.cost
    }));

    const totalCost = costFunction(optimalGroupInterval);

    return {
      optimalGroupInterval: parseFloat(optimalGroupInterval.toFixed(4)),
      totalCost: parseFloat(totalCost.toFixed(4)),
      taskSchedule,
      taskCount: tasks.length
    };
  }

  /**
   * Optimize using Delay Time Model (two-phase failure: defect arrival → failure)
   * Accounts for defects that arrive and then develop over time before failure
   *
   * @param {Object} params
   * @param {number} params.defectArrivalRate - λ (defects per unit time)
   * @param {number} params.delayTimeDistribution - { shape, scale } of delay time
   * @param {number} params.inspectionCost - Cost per inspection
   * @param {number} params.failureCost - Cost of undetected failure
   * @returns {Object} { optimalInspectionPeriod, expectedCostRate, defectsDetected }
   */
  optimizeDelayTime(params) {
    const { defectArrivalRate, delayTimeDistribution, inspectionCost, failureCost } = params;

    if (defectArrivalRate <= 0) {
      throw new Error('Defect arrival rate must be positive');
    }
    if (!this._validateWeibullParams(delayTimeDistribution.shape, delayTimeDistribution.scale)) {
      throw new Error('Invalid delay time distribution parameters');
    }

    const { shape, scale } = delayTimeDistribution;

    const costFunction = (T) => {
      if (T <= 0) return Infinity;

      // Number of inspections per unit time
      const inspectionFrequency = 1 / T;

      // Probability that a defect develops before next inspection
      const delayCDF_T = 1 - this.weibullReliability(T, shape, scale);

      // Expected number of undetected failures per unit time
      const undetectedFailures = defectArrivalRate * delayCDF_T;

      // Expected cost per unit time
      const expectedCost = inspectionFrequency * inspectionCost + undetectedFailures * failureCost;

      return expectedCost;
    };

    const meanDelayTime = this.weibullMTBF(shape, scale);
    const searchMin = meanDelayTime * 0.1;
    const searchMax = meanDelayTime * 2;

    const optimalInspectionPeriod = this.goldenSectionSearch(
      costFunction,
      searchMin,
      searchMax,
      1e-4
    );

    const delayProb = 1 - this.weibullReliability(optimalInspectionPeriod, shape, scale);
    const expectedCostRate = costFunction(optimalInspectionPeriod);
    const defectsDetected = defectArrivalRate * (1 - delayProb);

    return {
      optimalInspectionPeriod: parseFloat(optimalInspectionPeriod.toFixed(4)),
      expectedCostRate: parseFloat(expectedCostRate.toFixed(4)),
      defectsDetected: parseFloat(defectsDetected.toFixed(4)),
      defectArrivalRate,
      meanDelayTime: parseFloat(meanDelayTime.toFixed(4)),
      probabilityDefectDevelops: parseFloat(delayProb.toFixed(4))
    };
  }

  /**
   * Sensitivity Analysis - vary one parameter and return optimization results across range
   *
   * @param {string} optimizationType - 'arp', 'block', 'cbm', 'group', or 'delayTime'
   * @param {Object} baseParams - Base parameters for optimization
   * @param {string} paramToVary - Name of parameter to vary
   * @param {Object} range - { min, max } for parameter values
   * @param {number} steps - Number of steps in sensitivity analysis
   * @returns {Array} Array of { paramValue, result } objects
   */
  sensitivityAnalysis(optimizationType, baseParams, paramToVary, range, steps = 10) {
    if (!range || range.min >= range.max || steps < 2) {
      throw new Error('Invalid range or steps');
    }

    const results = [];
    const stepSize = (range.max - range.min) / (steps - 1);

    let optimizationFn;
    switch (optimizationType.toLowerCase()) {
      case 'arp':
        optimizationFn = (params) => this.optimizeAgeReplacement(params);
        break;
      case 'block':
        optimizationFn = (params) => this.optimizeBlockReplacement(params);
        break;
      case 'cbm':
        optimizationFn = (params) => this.optimizeCBM(params);
        break;
      case 'group':
        optimizationFn = (params) => this.optimizeGroupInterval(params);
        break;
      case 'delaytime':
        optimizationFn = (params) => this.optimizeDelayTime(params);
        break;
      default:
        throw new Error(`Unknown optimization type: ${optimizationType}`);
    }

    for (let i = 0; i < steps; i++) {
      const paramValue = range.min + i * stepSize;
      const testParams = { ...baseParams, [paramToVary]: paramValue };

      try {
        const result = optimizationFn(testParams);
        results.push({
          paramValue: parseFloat(paramValue.toFixed(4)),
          [paramToVary]: parseFloat(paramValue.toFixed(4)),
          ...result
        });
      } catch (error) {
        results.push({
          paramValue: parseFloat(paramValue.toFixed(4)),
          error: error.message
        });
      }
    }

    return results;
  }

  // ============================================================================
  // HELPER FUNCTIONS - Weibull Distribution
  // ============================================================================

  /**
   * Weibull Reliability function: R(t) = exp(-(t/η)^β)
   *
   * @param {number} t - Time
   * @param {number} shape - Shape parameter (β)
   * @param {number} scale - Scale parameter (η)
   * @returns {number} Reliability at time t
   */
  weibullReliability(t, shape, scale) {
    if (t < 0 || shape <= 0 || scale <= 0) return 0;
    if (t === 0) return 1;

    const ratio = t / scale;
    const exponent = Math.pow(ratio, shape);

    // Use log-space for numerical stability with small exponents
    if (exponent > 700) return 0;

    return Math.exp(-exponent);
  }

  /**
   * Weibull Probability Density Function: f(t) = (β/η) * (t/η)^(β-1) * exp(-(t/η)^β)
   *
   * @param {number} t - Time
   * @param {number} shape - Shape parameter (β)
   * @param {number} scale - Scale parameter (η)
   * @returns {number} PDF at time t
   */
  weibullPDF(t, shape, scale) {
    if (t < 0 || shape <= 0 || scale <= 0) return 0;
    if (t === 0) return shape === 1 ? 1 / scale : 0;

    const ratio = t / scale;
    const exponent = Math.pow(ratio, shape);

    if (exponent > 700) return 0;

    const coefficient = (shape / scale) * Math.pow(ratio, shape - 1);
    const exponentialTerm = Math.exp(-exponent);

    return coefficient * exponentialTerm;
  }

  /**
   * Weibull Hazard Rate function: h(t) = (β/η) * (t/η)^(β-1)
   *
   * @param {number} t - Time
   * @param {number} shape - Shape parameter (β)
   * @param {number} scale - Scale parameter (η)
   * @returns {number} Hazard rate at time t
   */
  weibullHazardRate(t, shape, scale) {
    if (t < 0 || shape <= 0 || scale <= 0) return 0;
    if (t === 0) return shape === 1 ? 1 / scale : 0;

    const ratio = t / scale;
    return (shape / scale) * Math.pow(ratio, shape - 1);
  }

  /**
   * Weibull Mean Time Between Failures (MTBF)
   * MTBF = η * Γ(1 + 1/β)
   *
   * @param {number} shape - Shape parameter (β)
   * @param {number} scale - Scale parameter (η)
   * @returns {number} Mean time between failures
   */
  weibullMTBF(shape, scale) {
    if (shape <= 0 || scale <= 0) {
      throw new Error('Weibull parameters must be positive');
    }

    const gammaArg = 1 + 1 / shape;
    const gammaValue = this.gammaFunction(gammaArg);

    return scale * gammaValue;
  }

  /**
   * Gamma Function using Stirling's approximation
   * More accurate for smaller values, good for reliability calculations
   *
   * @param {number} z - Argument
   * @returns {number} Gamma(z)
   */
  gammaFunction(z) {
    if (z <= 0) {
      throw new Error('Gamma function undefined for z <= 0');
    }

    // Use built-in approximation for common cases
    if (Math.abs(z - 0.5) < 1e-10) {
      return Math.sqrt(Math.PI);
    }
    if (Math.abs(z - 1) < 1e-10) {
      return 1;
    }
    if (Math.abs(z - 1.5) < 1e-10) {
      return Math.sqrt(Math.PI) / 2;
    }
    if (Math.abs(z - 2) < 1e-10) {
      return 1;
    }

    // Stirling's approximation: ln(Γ(z)) ≈ (z - 0.5)ln(z) - z + 0.5ln(2π)
    const pi = Math.PI;
    const lnTwoPi = Math.log(2 * pi);

    const lnGamma = (z - 0.5) * Math.log(z) - z + 0.5 * lnTwoPi;

    return Math.exp(lnGamma);
  }

  /**
   * Truncated MTBF for age-based replacement
   * Expected time until failure, given that we perform preventive replacement at time t
   *
   * @private
   * @param {number} t - Replacement time
   * @param {number} shape - Shape parameter
   * @param {number} scale - Scale parameter
   * @returns {number} Truncated MTBF
   */
  _weibullMTBFTruncated(t, shape, scale) {
    if (t <= 0 || shape <= 0 || scale <= 0) return 0;

    // Numerical integration of t * f(t) from 0 to t
    // For practical purposes: approximate as integral of R(t) from 0 to t
    const steps = 100;
    let sum = 0;
    const dt = t / steps;

    for (let i = 0; i < steps; i++) {
      const ti = i * dt;
      sum += this.weibullReliability(ti, shape, scale) * dt;
    }

    return sum;
  }

  /**
   * Validate Weibull parameters
   *
   * @private
   * @param {number} shape - Shape parameter (β)
   * @param {number} scale - Scale parameter (η)
   * @returns {boolean} True if valid
   */
  _validateWeibullParams(shape, scale) {
    return typeof shape === 'number' && typeof scale === 'number' &&
           shape > 0 && scale > 0 && isFinite(shape) && isFinite(scale);
  }

  // ============================================================================
  // OPTIMIZATION ALGORITHM - Golden Section Search
  // ============================================================================

  /**
   * Golden Section Search for 1D optimization
   * Finds minimum of unimodal function on [a, b]
   *
   * @param {Function} fn - Function to minimize
   * @param {number} a - Lower bound
   * @param {number} b - Upper bound
   * @param {number} tolerance - Convergence tolerance
   * @returns {number} x that minimizes fn(x)
   */
  goldenSectionSearch(fn, a, b, tolerance = 1e-5) {
    if (typeof fn !== 'function') {
      throw new Error('First argument must be a function');
    }
    if (a >= b) {
      throw new Error('Lower bound must be less than upper bound');
    }
    if (tolerance <= 0) {
      throw new Error('Tolerance must be positive');
    }

    const phi = (1 + Math.sqrt(5)) / 2; // Golden ratio ≈ 1.618
    const resphi = 2 - phi; // ≈ 0.381

    let x1 = a + resphi * (b - a);
    let x2 = b - resphi * (b - a);

    let f1 = fn(x1);
    let f2 = fn(x2);

    const maxIterations = 1000;
    let iterations = 0;

    while (Math.abs(b - a) > tolerance && iterations < maxIterations) {
      iterations++;

      if (f1 < f2) {
        b = x2;
        x2 = x1;
        f2 = f1;
        x1 = a + resphi * (b - a);
        f1 = fn(x1);
      } else {
        a = x1;
        x1 = x2;
        f1 = f2;
        x2 = b - resphi * (b - a);
        f2 = fn(x2);
      }
    }

    return (a + b) / 2;
  }
}

export default IntervalOptimization;
